import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../shared/api/client";
import type { ContractDetail } from "./contract-types";

type Dependencies = {
  detail: ContractDetail | null;
  selectedId: string;
  scopeKey: string;
  setDetail: (detail: ContractDetail) => void;
  load: () => Promise<void>;
  notify: (message: string) => void;
};
export function useContractActionsWorkspace({ detail, selectedId, scopeKey, setDetail, load, notify }: Dependencies) {
  const [decision, setDecision] = useState<"APPROVED" | "REJECTED">("APPROVED");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  const current = useRef({ selectedId, scopeKey, generation: 0 });
  if (current.current.selectedId !== selectedId || current.current.scopeKey !== scopeKey) {
    current.current = { selectedId, scopeKey, generation: current.current.generation + 1 };
  }
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setReason(""); }, [selectedId, scopeKey]);

  async function write(kind: "decision" | "submit") {
    if (!detail || detail.id !== selectedId || busy.current || (kind === "decision" && reason.trim().length < 10)) return;
    busy.current = true;
    setSubmitting(true);
    const generation = current.current.generation;
    const isCurrent = () => mounted.current && current.current.generation === generation;
    try {
      const updated = await apiRequest<ContractDetail>(`/api/contracts/${detail.id}/${kind === "decision" ? "decisions" : "submit"}`, {
        method: "POST",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify(kind === "decision" ? { decision, reason: reason.trim(), expectedVersion: detail.currentVersion } : { expectedVersion: detail.currentVersion }),
      });
      if (!isCurrent()) return;
      setDetail(updated);
      if (kind === "decision") setReason("");
      notify(kind === "submit" ? "Sözleşme yetkili onayına gönderildi." : decision === "APPROVED" ? "Sözleşme kararı kaydedildi." : "Sözleşme düzeltme için geri gönderildi.");
      await load();
    } catch (caught) {
      if (isCurrent()) notify(caught instanceof Error ? caught.message : kind === "decision" ? "Karar kaydedilemedi." : "Sözleşme gönderilemedi.");
    } finally {
      busy.current = false;
      if (mounted.current) setSubmitting(false);
    }
  }
  return { decision, setDecision, reason, setReason, submitting, saveDecision: () => write("decision"), submitDraft: () => write("submit") };
}
